import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SearchSelector } from '@/features/menu/components/SearchSelector/SearchSelector';
import { BrandLogo } from '@/core/components/BrandLogo/BrandLogo';
import { BdsIcon } from '@/core/constants/webComponents';
import { HEADER_EVENT_NAMES } from '@/features/header/constants/eventNames';
import { MetricsStaticLinks, type StaticLinkItem } from '@/features/metrics-menu/components/MetricsStaticLinks/MetricsStaticLinks';
import { staticMetricsPaths } from '../../metricsMenuPaths';

import './MetricsHeader.scss';

interface MetricsHeaderProps {
  accountName?: string;
  profileImage?: string;
  searchOptions?: Array<any>;
  selectedSearch?: any;
  universeId?: string;
  onBackClick?: () => void;
  onToggleSidebar?: () => void;
}

const getStaticLinksData = (t: TFunction): StaticLinkItem[] => [
  {
    id: 'title-metrics',
    label: t('metricsMenu.proprietaryMetrics'),
    isTitle: true
  },
  {
    id: 'link-benchmarks',
    label: t('metricsMenu.benchmarks'),
    href: staticMetricsPaths.benchmarks,
    icon: 'chart-column',
  },
  {
    id: 'link-connections',
    label: t('metricsMenu.myConnections'),
    icon: 'plugin',
  },
  {
    id: 'link-reports',
    label: t('metricsMenu.conversationsReport'),
    href: staticMetricsPaths.conversationsReport,
    icon: 'message-total',
  },
  {
    id: 'link-settings',
    label: t('metricsMenu.settings'),
    href: staticMetricsPaths.settings,
    icon: 'settings-general',
  }
];

export const MetricsHeader: React.FC<MetricsHeaderProps> = ({
  accountName,
  profileImage,
  searchOptions,
  selectedSearch,
  universeId,
  onBackClick,
  onToggleSidebar,
}) => {
  const { t } = useTranslation();
  const staticLinksData = getStaticLinksData(t);

  const handleBackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const eventTarget = event.currentTarget ?? document;
    eventTarget.dispatchEvent(
      new CustomEvent('mfe-metrics-back-click', {
        detail: {},
        bubbles: true,
        composed: true,
      }),
    );
    onBackClick?.();
  };

  const handleToggleSidebar = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onToggleSidebar) {
      onToggleSidebar();
      return;
    }

    event.currentTarget.dispatchEvent(
      new CustomEvent(HEADER_EVENT_NAMES.SIDEBAR_TOGGLE, {
        bubbles: true,
        composed: true,
      }),
    );
  };

  return (
    <div className="metrics-header">
      <div className="metrics-header__brand-row">
        <button
          type="button"
          className="metrics-header__toggle"
          aria-label={t('header.toggleSidebar')}
          title={t('header.toggleSidebar')}
          onClick={handleToggleSidebar}
        >
          <BdsIcon name="menu-hamburger" color="surface-0" size="medium" />
        </button>
        <BrandLogo
          className="metrics-header__brand"
          illustrationWrapperClassName="metrics-header__brand-blip"
          illustrationClassName="metrics-header__brand-icon"
          altText={t('common.brandIcon')}
        />
      </div>

      <div className="metrics-header__back-button">
          <button
            type="button"
            className="metrics-header__back"
            onClick={handleBackClick}
          >
            <BdsIcon name="arrow-ball-left" color="surface-0" />
            <span>{t('newSetup.back')}</span>
          </button>
      </div>

      <div className="metrics-header__back-row">
        <SearchSelector
          profileName={accountName}
          avatarUrl={profileImage}
          searchOptions={searchOptions}
          selectedSearch={selectedSearch}
          universeId={universeId}
        />
      </div>

      <MetricsStaticLinks items={staticLinksData} />
    </div>
  );
};